import { isArrayEmpty } from 'util/isArrayEmpty';
import { createResourceContentSelector } from 'util/selector/resourceSelector';

import { createSelector } from '@reduxjs/toolkit';
import { EmailTemplateModel } from 'api/dto/EmailTemplate.dto';
import { AdminState } from 'reduxStore/appStore';
import { EmailDistributionState } from 'reduxStore/emailDistribution/initialState';
import { selectCurrentProject, selectCurrentSubProject } from 'reduxStore/project/projectSelector';
import { ProjectModel } from 'api/dto/Project.dto';

export const selectEmailDomain = (state: AdminState) =>
  state.emailDistribution as EmailDistributionState.Domain;

export const selectEmailDistributionResource = createSelector(
  selectEmailDomain,
  (domain) => domain.distributionResource,
);

export const selectEmailDistribution = createResourceContentSelector(
  selectEmailDistributionResource,
);

export const selectEmailDistributionId = createSelector(
  selectEmailDistribution,
  (emailDistribution) => emailDistribution?.id,
);

export const selectInvitation = createSelector(
  selectEmailDistribution,
  (emailDistribution) => emailDistribution?.invitation,
);

export const selectEmailTemplateByIdByLanguage = createSelector(
  selectEmailDomain,
  (domain) => domain.emailTemplateByIdByLanguage,
);

export const selectCurrentEmailTemplate = createSelector(
  selectEmailDomain,
  (domain) => domain.currentEmailTemplate,
);

export const selectInvitationSchedules = createSelector(
  selectInvitation,
  (invitation) => invitation?.schedules ?? [],
);

export const selectHasInvitationAtLeastOneScheduleSent = createSelector(
  selectInvitationSchedules,
  (schedules) => !!schedules.find((schedule) => !!schedule.lastSent),
);

export const selectIsScheduleEditable = createSelector(selectCurrentProject, (project) => {
  return (
    (ProjectModel.isEditable(project) && Boolean(project.liveStop && project.liveStart)) ||
    ProjectModel.isLive(project)
  );
});

export const selectIsScheduleEditableSubProjects = createSelector(
  selectCurrentSubProject,
  (currentProject) => {
    if (!currentProject) {
      return false;
    }

    return (
      (ProjectModel.isEditable(currentProject) &&
        Boolean(currentProject.liveStop && currentProject.liveStart)) ||
      ProjectModel.isLive(currentProject)
    );
  },
);

export const selectReminders = createSelector(
  selectEmailDistribution,
  (distribution) => distribution?.reminders ?? [],
);

export const selectFirstReminder = createSelector(selectReminders, (reminders) =>
  !isArrayEmpty(reminders) ? reminders[0] : null,
);

export const selectIsEmailSent = (email: EmailTemplateModel) => {
  const schedules = email?.schedules;

  if (isArrayEmpty(schedules)) {
    return false;
  }

  const firstSchedule = schedules[0];

  return firstSchedule ? !!firstSchedule.lastSent : false;
};

export const selectIsReminderAlreadySent = createSelector(selectFirstReminder, selectIsEmailSent);
